import { ChangeDetectionStrategy, Component, Signal, computed, effect, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { StoreRequest } from '@bn/shared/state-store';
import { BadgeComponent } from '@bn/web-ui/badge/badge.component';
import { BadgeColor } from '@bn/web-ui/badge/models';
import { FormListItem } from '@bn/web-ui/models';
import { RadioTabsComponent } from '@bn/web-ui/tabs/radio-tabs.component';
import { BotCategorizationAction, BotCategorizationDetails, BotCategorizationGroup, BotCategoryAction } from '../../../openapi';
import { ShieldBotCategorizationApiStore } from '../../../stores';
import { BOT_POLICY_OPTIONS, BotCategoryMeta, BotPolicyModifier, getBotCategoryMeta, getBotPolicyOption, getBotPolicyOptionByGroupAction } from '../models';

interface BotGroupPill {
  color: BadgeColor;
  count: number;
  label: string;
  modifier: BotPolicyModifier;
}

const PILL_WORD: Record<BotPolicyModifier, string> = {
  allow: 'allowed',
  ignore: 'ignored',
  block: 'blocked',
};

const PILL_COLOR: Record<BotPolicyModifier, BadgeColor> = {
  allow: 'green',
  ignore: 'grey',
  block: 'red',
};

// Known name segments recased to their real-world form, e.g. "pinterestbot" -> "PinterestBot", "gptbot" -> "GPTBot".
const BOT_NAME_SEGMENTS = /(bot|gpt|ai|app)/gi;

const BOT_NAME_SEGMENT_CASING: Record<string, string> = {
  ai: 'AI',
  app: 'App',
  bot: 'Bot',
  gpt: 'GPT',
};

@Component({
  selector: 'app-shield-bot-controls-group',
  templateUrl: './app-shield-bot-controls-group.component.html',
  styleUrl: './app-shield-bot-controls-group.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BadgeComponent, FaIconComponent, FormsModule, RadioTabsComponent, TooltipDirective],
})
export class AppShieldBotControlsGroupComponent {
  readonly #store = inject(ShieldBotCategorizationApiStore);
  readonly #botLoadingCache = new Map<number, Signal<boolean>>();

  readonly #pendingBotActions = signal<ReadonlyMap<number, BotCategorizationAction>>(new Map());
  readonly #pendingCategoryAction = signal<BotCategoryAction | null>(null);
  readonly #botsSeenLoading = new Set<number>();
  #categorySeenLoading = false;

  constructor() {
    effect(() => {
      for (const botId of this.#pendingBotActions().keys()) {
        if (this.botLoading(botId)()) {
          this.#botsSeenLoading.add(botId);
        } else if (this.#botsSeenLoading.delete(botId)) {
          this.#pendingBotActions.update((pending) => {
            const next = new Map(pending);

            next.delete(botId);

            return next;
          });
        }
      }
    });

    effect(() => {
      if (this.#pendingCategoryAction() === null) return;

      if (this.categoryLoading()) {
        this.#categorySeenLoading = true;
      } else if (this.#categorySeenLoading) {
        this.#categorySeenLoading = false;
        this.#pendingCategoryAction.set(null);
      }
    });
  }

  readonly group = input.required<BotCategorizationGroup>();
  readonly shieldZoneId = input.required<number>();
  readonly disabled = input(false);
  readonly forceOpen = input(false);

  protected readonly meta = computed<BotCategoryMeta>(() => getBotCategoryMeta(this.group().category));
  protected readonly bots = computed<BotCategorizationDetails[]>(() => this.group().bots ?? []);
  protected readonly summaryPills = computed<BotGroupPill[]>(() =>
    this.#buildPills(
      this.bots().map((bot) => ({
        ...bot,
        action: this.effectiveAction(bot),
      })),
    ),
  );
  protected readonly categoryLoading = computed(() => this.#store.isLoading(StoreRequest.Update, [`category-${this.group().category}`])());
  protected readonly bulkValue = computed<BotCategoryAction | null>(() => this.group().categoryAction ?? null);
  protected readonly overrideCount = computed(() => this.bots().filter((bot) => this.isOverride(bot)).length);

  readonly #categoryBotAction = computed<BotCategorizationAction>(() => getBotPolicyOptionByGroupAction(this.bulkValue()).action);

  protected effectiveAction(bot: BotCategorizationDetails): BotCategorizationAction {
    return this.isOverride(bot) ? bot.action : this.#categoryBotAction();
  }

  protected displayedAction(bot: BotCategorizationDetails): BotCategorizationAction {
    return this.#pendingBotActions().get(bot.botId) ?? this.effectiveAction(bot);
  }

  protected readonly displayedCategoryAction = computed<BotCategoryAction | null>(() => this.#pendingCategoryAction() ?? this.bulkValue());

  // Policy options mapped for bn-web-ui-radio-tabs; the option icon shows as the active-item checkmark.
  protected readonly policyItems: FormListItem[] = BOT_POLICY_OPTIONS.map((option) => ({
    label: option.label,
    name: option.label,
    sideIcon: option.icon,
    value: option.action,
  }));

  protected readonly groupPolicyItems: FormListItem[] = BOT_POLICY_OPTIONS.map((option) => ({
    label: option.label,
    name: option.label,
    sideIcon: option.icon,
    value: option.groupAction,
  }));

  readonly #open = signal(false);
  protected readonly isOpen = computed(() => this.forceOpen() || this.#open());

  protected toggle(): void {
    if (this.forceOpen()) return;

    this.#open.update((value) => !value);
  }

  protected botDisplayName(name: string | null): string {
    if (!name) return '';

    const cased = name.replace(BOT_NAME_SEGMENTS, (segment) => BOT_NAME_SEGMENT_CASING[segment.toLowerCase()]);

    return cased.charAt(0).toUpperCase() + cased.slice(1);
  }

  // A bot overrides the category default when it has an explicit action (None = inherit) that differs from it.
  protected isOverride(bot: BotCategorizationDetails): boolean {
    return bot.action !== BotCategorizationAction.None && bot.action !== this.#categoryBotAction();
  }

  // Per-bot items: an inherited bot gets its active tab disabled too, rendering it as a muted "ghost" selection.
  protected botPolicyItems(bot: BotCategorizationDetails): FormListItem[] {
    const effectiveAction = this.effectiveAction(bot);

    return this.policyItems.map((item) => ({
      ...item,
      disabled: !this.isOverride(bot) && item.value === effectiveAction,
    }));
  }

  // Clearing an override re-inherits the bot by setting its action back to the current category default.
  protected clearOverride(bot: BotCategorizationDetails): void {
    this.#store.setBotAction({ shieldZoneId: this.shieldZoneId(), botId: bot.botId, action: BotCategorizationAction.None });
  }

  // Re-inherit every overridden bot in this category in one action.
  protected clearAllOverrides(): void {
    this.#store.clearCategoryOverrides({ shieldZoneId: this.shieldZoneId(), category: this.group().category });
  }

  protected botLoading(botId: number): Signal<boolean> {
    let loading = this.#botLoadingCache.get(botId);

    if (!loading) {
      loading = this.#store.isLoading(StoreRequest.Update, [`bot-${botId}`]);
      this.#botLoadingCache.set(botId, loading);
    }

    return loading;
  }

  // radio-tabs emits tabItemClickedEvent for every item click (even the active or disabled one),
  // so both handlers guard against no-op and mid-request clicks before hitting the store.
  protected onCategoryTab(item: FormListItem): void {
    const action = item.value as BotCategoryAction;

    if (this.disabled() || this.categoryLoading() || action === this.displayedCategoryAction()) return;

    this.#pendingCategoryAction.set(action);
    this.#store.setCategoryAction({ shieldZoneId: this.shieldZoneId(), category: this.group().category, action });
  }

  protected onPolicyTabChange(bot: BotCategorizationDetails, item: FormListItem): void {
    const action = item.value as BotCategorizationAction;

    if (this.disabled() || this.botLoading(bot.botId)() || this.categoryLoading() || action === this.displayedAction(bot)) {
      return;
    }

    this.#pendingBotActions.update((pending) => new Map(pending).set(bot.botId, action));
    this.#store.setBotAction({
      shieldZoneId: this.shieldZoneId(),
      botId: bot.botId,
      action,
    });
  }

  #buildPills(bots: BotCategorizationDetails[]): BotGroupPill[] {
    const counts = new Map<BotPolicyModifier, number>();

    bots.forEach((bot) => {
      const { modifier } = getBotPolicyOption(bot.action);

      counts.set(modifier, (counts.get(modifier) ?? 0) + 1);
    });

    return BOT_POLICY_OPTIONS.filter((option) => (counts.get(option.modifier) ?? 0) > 0).map((option) => {
      const count = counts.get(option.modifier) ?? 0;

      return { color: PILL_COLOR[option.modifier], count, label: `${count} ${PILL_WORD[option.modifier]}`, modifier: option.modifier };
    });
  }
}
